// THE CONDITION AND COMPS STEPS' PRESENTATION RULES (WEB-PARITY-3). Which of the app's own sentences a server fact
// selects, and how a figure is printed — each a port of the Swift line it cites. Nothing here judges a comp, a photo or a
// value: those are the server's (`value/compCards.ts`, `comps/usable.ts`, NEW-21), and arrive on the response.
//
// THE ONE EXCEPTION, RECORDED AT BIRTH (docs/THIN_CLIENT_DEBT.md, WEB-PARITY-3): the add/edit sheet's three warnings
// (`sheetWarnings`) and its Save gate (`sheetSavable`) answer the owner's typing BEFORE anything is sent — the app's sheet
// does the same, on the device (CompEditView.swift:118-136, :232) — so they cannot wait for a server round trip.

import type { CompView, PropertyTypeCode, SubjectView, ValueResponse } from "@contract";
import { say, sayWith } from "./parity3Wording";

type CardFacts = ValueResponse["body"]["value"]["comps"][number];

// ── the property ──────────────────────────────────────────────────────────────────────────────────────────────────────

/** `SubjectProperty.isAttachedOrMultiUnit` (Models.swift:562-565) over the app's tag for the owner's kind: a condo, or a
 *  multi-unit building ("Multi-unit" is the app's "Multi-family"). */
export function isUnitInBuilding(type: PropertyTypeCode | null | undefined): boolean {
  return type === "Condo" || type === "Multi-unit";
}

// ── /condition ────────────────────────────────────────────────────────────────────────────────────────────────────────

/** `ConditionEvidenceCard.add(_:)` (CaseEvidenceCards.swift:78-84): the chip becomes the text, or is appended with "; "
 *  unless the text already holds it (case-insensitively). */
export function withChip(text: string, chip: string): string {
  if (text.trim() === "") return chip;
  if (text.toLowerCase().includes(chip.toLowerCase())) return text;
  return `${text}; ${chip}`;
}

/** The app's per-pick limit, which is also the contract's whole-list maximum (`photos`, max 8; CaseEvidenceCards.swift:143). */
export const PHOTO_LIMIT = 8;

// ── /comps: the search's result line (CompsView.swift:736-768) ───────────────────────────────────────────────────────

/** The app keeps its progress lines up at least four seconds, so a quick search still reads (CompsView.swift:804-809).
 *  A test that is not about the wait sets it to 0. */
export const searchTiming = { minimumMs: 4000 };

/** How long each of the first three lines of step 3's loading card (`Comps.tsx` `CompsLoading`, 2026-10-14) is the
 *  current one before the next. The last stays current until the automatic search answers. A setting, so a test can see the order
 *  without waiting it out. */
export const compsLoadingTiming = { lineMs: 2500 };

export interface SearchResult {
  readonly text: string;
  readonly tone: "success" | "info";
}

/** The sentence the app sets after a search that ran: how many it added, or why none came back. `isIncomeProperty` is
 *  false on both clients while income is dark (SuggestedValue.swift:1481-1485), so the rental branch is never taken. */
export function searchResult(meta: { ranked: number; candidatesConsidered: number; typeFilteredOut: number }, unitInBuilding: boolean): SearchResult {
  const n = meta.ranked;
  if (n > 0) {
    return {
      tone: "success",
      text: sayWith("comps.result.added", { n, matchWord: say(n === 1 ? "comps.result.added.match" : "comps.result.added.matches") }),
    };
  }
  const seen = meta.candidatesConsidered;
  if (meta.typeFilteredOut > 0 && unitInBuilding) {
    const t = meta.typeFilteredOut;
    const itThem = say(t === 1 ? "comps.result.typeFiltered.it" : "comps.result.typeFiltered.them");
    const text = sayWith("comps.result.typeFiltered", {
      seen,
      saleWord: say(seen === 1 ? "comps.result.typeFiltered.sale" : "comps.result.typeFiltered.sales"),
      notType: t === 1 ? say("comps.result.typeFiltered.itWasnt") : sayWith("comps.result.typeFiltered.nWerent", { n: t }),
      kind: say("comps.result.typeFiltered.sameType"),
      it: itThem,
      it2: itThem,
    });
    return { tone: "info", text: text + say("comps.result.typeFiltered.addSimilar") };
  }
  if (seen > 0) {
    return {
      tone: "info",
      text: sayWith("comps.result.noPrice", {
        seen, homeWord: say(seen === 1 ? "comps.result.noPrice.home" : "comps.result.noPrice.homes"),
      }),
    };
  }
  return { tone: "info", text: say("comps.result.none") };
}

/** `candidatesReferenceText(shown:seen:)` (CompsView.swift:401-407): both numbers when the search looked at more than it
 *  ranked, else the one. */
export function candidatesReference(shown: number, seen: number): string {
  if (seen > shown) return sayWith("comps.reference.seen", { seen, shown });
  return sayWith("comps.reference.oneNumber", {
    shown, sales: say(shown === 1 ? "comps.reference.sale" : "comps.reference.sales"),
  });
}

// ── the card's figures, printed as the app prints them ──────────────────────────────────────────────────────────────

const WHOLE_DOLLARS = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const MONTHS_LONG = [
  "January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December",
];

/** `Double.currencyString`: whole dollars. */
export function dollars(amount: number): string {
  return WHOLE_DOLLARS.format(amount);
}

function dayParts(iso: string): { y: number; m: number; d: number } | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (match === null) {
    const compact = /^(\d{4})(\d{2})(\d{2})$/.exec(iso) ?? /^(\d{4})\/(\d{2})\/(\d{2})$/.exec(iso);
    if (compact === null) return null;
    return { y: Number(compact[1]), m: Number(compact[2]), d: Number(compact[3]) };
  }
  return { y: Number(match[1]), m: Number(match[2]), d: Number(match[3]) };
}

/** `.dateTime.month(.wide).day().year()` — "January 1, 2026" (the banner, CompsView.swift:63). */
export function wideDate(iso: string): string {
  const p = dayParts(iso);
  return p === null ? iso : `${MONTHS_LONG[p.m - 1]} ${p.d}, ${p.y}`;
}

/** `.dateTime.month(.abbreviated).day().year()` — "Jan 1, 2026" (the recency row, :1108). */
export function abbreviatedDate(iso: string): string {
  const p = dayParts(iso);
  return p === null ? iso : `${MONTHS_SHORT[p.m - 1]} ${p.d}, ${p.y}`;
}

/** `.dateTime.month(.abbreviated).year()` — "Jun 2025" (the date pill, :954). */
export function monthYear(iso: string): string {
  const p = dayParts(iso);
  return p === null ? iso : `${MONTHS_SHORT[p.m - 1]} ${p.y}`;
}

/** `sqftLabel(_:)` (:1151-1157): the size, and its difference from the subject's when there is one. */
export function sqftLabel(sqft: number, subjectSqft: number | null): string {
  const base = sayWith("card.sqft", { sqft: Math.trunc(sqft) });
  if (subjectSqft === null || !(subjectSqft > 0)) return base;
  const delta = Math.trunc(Math.round(sqft - subjectSqft));
  if (delta === 0) return base;
  return `${base} (${delta > 0 ? "+" : ""}${delta})`;
}

/** `bathLabel(_:)` and `num(_:)` (:1159-1165): a whole number plainly, else one decimal. */
export function oneDecimal(v: number): string {
  return v === Math.round(v) ? String(Math.trunc(v)) : v.toFixed(1);
}

/** "Sold …" (:1105-1126), from the server's recency facts. */
export function soldSentence(recency: NonNullable<CardFacts["recency"]>, valuationDate: string): string {
  const date = abbreviatedDate(valuationDate);
  if (recency.months === 0) return sayWith("card.sold", { relation: sayWith("card.sold.at", { date }) });
  const unit = say(recency.months === 1 ? "card.sold.month" : "card.sold.months");
  const relation = sayWith(recency.before ? "card.sold.before" : "card.sold.after", { months: recency.months, unit, date });
  return sayWith("card.sold", { relation });
}

/** The time line (:984): the price, and the market's move since the sale to one decimal, signed. */
export function timeAdjustedSentence(t: NonNullable<CardFacts["timeAdjusted"]>): string {
  return sayWith("card.timeAdjusted", {
    price: dollars(t.price.amount), sign: t.percent >= 0 ? "+" : "", pct: t.percent.toFixed(1),
  });
}

/** The size line (:1024-1043). */
export function sizeSentence(line: NonNullable<CardFacts["sizeLine"]>): string {
  const adjusted = dollars(line.adjusted.amount);
  switch (line.kind) {
    case "trimmed": return sayWith("card.size.line", { adjusted, note: say("card.size.trimmed") });
    case "raised": return sayWith("card.size.line", { adjusted, note: say("card.size.raised") });
    case "same": return sayWith("card.size.line", { adjusted, note: say("card.size.same") });
    case "notSizedTimeAdjusted": return sayWith("card.size.notSizedTimeAdjusted", { adjusted });
    case "notSizedAsSold": return sayWith("card.size.notSizedAsSold", { adjusted });
  }
}

// ── the add/edit sheet (CompEditView.swift) — see this file's header for why these run on the client ────────────────

export interface SheetDraft {
  address: string;
  salePrice: number | null;
  hasSaleDate: boolean;
  saleDate: string;
  sqft: number | null;
  beds: number | null;
  baths: number | null;
  distanceMi: number | null;
  /** Filled by the lookup only; the sheet has no field for it (CompEditView.swift:106). */
  yearBuilt: number | null;
  included: boolean;
}

/** `comp.isUsable` (Models.swift:1653), the Save gate (CompEditView.swift:232): an address and a price. The server's
 *  `isUsable` (functions/src/v2/comps/usable.ts) judges the saved comp again; this only enables the button. */
export function sheetSavable(draft: SheetDraft): boolean {
  return draft.address.trim() !== "" && draft.salePrice !== null;
}

/** The three honest, non-blocking warnings (CompEditView.swift:118-136), in the app's order.
 *
 *  ONE IS NOT COPIED AS THE APP HAS IT (D9, "the 'two years old' warning measures to today (CE:127)"): a property-tax
 *  appeal is judged as of its valuation date, and the comps step measures every sale to it (CompMatch.swift:8-13), so the
 *  sheet does too. With no valuation date known yet, it says nothing rather than measuring to today. */
export function sheetWarnings(
  draft: SheetDraft, subjectBasis: number | null, valuationDate: string | null,
): string[] {
  const out: string[] = [];
  if (draft.salePrice !== null && subjectBasis !== null && subjectBasis > 0 && draft.salePrice > subjectBasis) {
    out.push(sayWith("sheet.warn.aboveBasis", { price: dollars(draft.salePrice), basis: dollars(subjectBasis) }));
  }
  if (draft.hasSaleDate && draft.saleDate !== "" && valuationDate !== null) {
    const sale = dayParts(draft.saleDate);
    const asOf = dayParts(valuationDate);
    if (sale !== null && asOf !== null) {
      // Whole months from the sale to the valuation date, as `Calendar.dateComponents([.month], from:to:)` counts them.
      const months = (asOf.y - sale.y) * 12 + (asOf.m - sale.m) - (asOf.d < sale.d ? 1 : 0);
      if (months > 24) out.push(say("sheet.warn.stale"));
    }
  }
  if (draft.distanceMi !== null && draft.distanceMi > 3) {
    out.push(sayWith("sheet.warn.distance", { miles: draft.distanceMi.toFixed(1) }));
  }
  return out;
}

/** A stored comp as the sheet opens it (`CompEditView.init`, :36-47). */
export function draftOf(comp: CompView): SheetDraft {
  const day = comp.saleDate === null ? null : dayParts(comp.saleDate);
  return {
    address: comp.address,
    salePrice: comp.salePrice?.amount ?? null,
    hasSaleDate: day !== null,
    saleDate: day === null ? "" : `${day.y}-${String(day.m).padStart(2, "0")}-${String(day.d).padStart(2, "0")}`,
    sqft: comp.sqft,
    beds: comp.beds,
    baths: comp.baths,
    distanceMi: comp.distanceMi,
    yearBuilt: comp.yearBuilt ?? null,
    included: comp.included,
  };
}

/** A new owner comp (`Comp(ownerProvided: true)`, CompsView.swift:648): empty, included. */
export const EMPTY_DRAFT: SheetDraft = {
  address: "", salePrice: null, hasSaleDate: false, saleDate: "", sqft: null, beds: null, baths: null, distanceMi: null,
  yearBuilt: null, included: true,
};

/** The subject's market basis the above-basis warning compares against (`subjectBasis: …impliedMarketValue`,
 *  CompsView.swift:251). */
export function subjectBasisOf(subject: SubjectView | null): number | null {
  return subject?.impliedMarketValue?.amount ?? null;
}
